import { Box, IconButton, styled, useTheme } from '@mui/material';
import { ChevronLeft, ChevronRight, CircleIcon } from 'lucide-react';
import React, { ReactNode, useEffect, useState } from 'react';

const StyledCarouselButton = styled(IconButton)(({ theme }) => ({
  width: 50,
  height: 50,
  cursor: 'default',
  backgroundColor: theme.palette.darkBlue.main,
  color: theme.palette.beige.main,
  borderRadius: '50%',
  '&:hover': {
    opacity: '50%',
    backgroundColor: theme.palette.darkBlue.main,
    color: theme.palette.beige.main,
  },
  '&:focus': {
    backgroundColor: theme.palette.darkBlue.main,
    color: theme.palette.beige.main,
  },
}));

interface CarouselProps {
  children: ReactNode;
  autoPlay?: boolean;
  interval?: number;
}

const Carousel = ({ children, autoPlay = true, interval = 5000 }: CarouselProps) => {
  const theme = useTheme();

  const [activeStep, setActiveStep] = useState<number>(0);
  const maxSteps: number = React.Children.count(children);

  const handleNext = (): void => {
    setActiveStep((prevActiveStep) => (prevActiveStep + 1) % maxSteps);
  };

  const handleBack = (): void => {
    setActiveStep((prevActiveStep) => (prevActiveStep - 1 + maxSteps) % maxSteps);
  };

  const handleDotClick = (index: number): void => {
    setActiveStep(index);
  };

  // Effect hook for auto-play functionality
  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    if (autoPlay && maxSteps > 1) {
      // Set an interval to automatically advance slides
      timer = setInterval(() => {
        handleNext();
      }, interval);
    }

    // Cleanup function to clear the interval when the component unmounts
    // or when autoPlay/interval changes
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [activeStep, autoPlay, interval, maxSteps]);

  return (
    <Box
      sx={{
        width: '100%',
        height: '400px',
        overflow: 'hidden',
        position: 'relative',
      }}>
      {/* Inner container that will be translated to show the active slide */}
      <Box
        sx={{
          display: 'flex',
          height: '100%',
          transition: 'transform 250ms ease-in-out',
          transform: `translateX(-${activeStep * 100}%)`, // Translate based on activeStep
        }}>
        {React.Children.map(children, (child: ReactNode, index: number) => (
          <Box
            key={index}
            sx={{
              flexShrink: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
            {child}
          </Box>
        ))}
      </Box>
      {maxSteps > 1 && (
        // Container for navigation (MobileStepper for arrows and custom dots)
        (<Box
          sx={{
            width: '100%',
            backgroundColor: 'transparent',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            zIndex: 1, // Ensure the navigation is on top and clickable
          }}>
          {/* Back Button */}
          <StyledCarouselButton
            size="small"
            onClick={handleBack}
            disabled={maxSteps === 0} // Disable if no items
          >
            <ChevronLeft />
          </StyledCarouselButton>
          {/* Custom dot navigation in the center */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              flexGrow: 1, // Allow dots to take up available space in the center
              gap: 2,
            }}>
            {Array.from({ length: maxSteps }).map((_, index) => (
              <IconButton
                key={index}
                onClick={() => handleDotClick(index)}
                size="small"
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  border: `1px solid ${theme.palette.beige.main}`,
                  backgroundColor: activeStep === index ? theme.palette.beige.main : 'transparent',
                  '&:hover': {
                    backgroundColor: theme.palette.beige.main,
                    opacity: 0.8,
                  },
                  '& svg': {
                    color: activeStep === index ? theme.palette.beige.main : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                  },
                }}>
                <CircleIcon />
              </IconButton>
            ))}
          </Box>
          {/* Next Button */}
          <StyledCarouselButton size="small" onClick={handleNext} disabled={maxSteps === 0}>
            <ChevronRight />
          </StyledCarouselButton>
        </Box>)
      )}
    </Box>
  );
};

export default Carousel;
